import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { 
  Calculator, 
  FileText, 
  TrendingUp, 
  Wallet, 
  Handshake, 
  Users, 
  Settings, 
  RefreshCcw,
  Menu,
  X,
  Phone,
  Mail,
  Linkedin,
  ChevronRight,
  Check,
  Building2,
  ShoppingCart,
  Plane,
  Pill,
  Briefcase,
  Factory,
  Cookie,
  Shield,
  BookOpen
} from "lucide-react";
import { Button } from "./components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

// Lazy load blog and admin pages
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPostEditor from "./pages/AdminPostEditor";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";

// Services data
const services = [
  {
    icon: Calculator,
    title: "Bookkeeping & Management Accounts",
    description: "Monthly accounts, KPI tracking, cloud platforms integration for real-time financial visibility."
  },
  {
    icon: FileText,
    title: "Tax Planning & Compliance",
    description: "Corp Tax, VAT, PAYE, R&D credits, HMRC liaison — proactive strategy, not just filing."
  },
  {
    icon: TrendingUp,
    title: "Financial Planning & Analysis",
    description: "FP&A, scenario modelling, 13-week cash flow forecasts to drive strategic decisions."
  },
  {
    icon: Wallet,
    title: "Cash Flow & Working Capital",
    description: "Treasury management, banking relationships, covenant monitoring for financial resilience."
  },
  {
    icon: Handshake,
    title: "Mergers & Acquisitions",
    description: "End-to-end M&A support, £3m–£16m transactions, UK & international deal experience."
  },
  {
    icon: Users,
    title: "Finance Recruitment & Coaching",
    description: "Accountants recruiting accountants — from FD to bookkeeper, we find the right fit."
  },
  {
    icon: Settings,
    title: "Financial Systems & ERP",
    description: "Xero, QuickBooks, Sage, Navision implementation with up to 40% process automation."
  },
  {
    icon: RefreshCcw,
    title: "Turnaround & Restructuring",
    description: "Stabilisation, HMRC/bank negotiation, governance strengthening for businesses in distress."
  }
];

// Sectors data
const sectors = [
  { icon: Building2, title: "Hospitality & QSR", description: "Multi-site restaurant groups, quick-service chains, hotels" },
  { icon: ShoppingCart, title: "Retail & E-Commerce", description: "Omnichannel retailers, DTC brands, marketplace sellers" },
  { icon: Plane, title: "Aviation & Travel", description: "Airlines, travel operators, ground services" },
  { icon: Pill, title: "Pharmaceutical & Healthcare", description: "Pharma companies, healthcare providers, wellness brands" },
  { icon: Briefcase, title: "Professional Services", description: "Law firms, consultancies, agency groups" },
  { icon: Factory, title: "Trade & Manufacturing", description: "Manufacturing, distribution, trade businesses" }
];

// Stats data
const stats = [
  { number: "17", label: "Years of CFO-level expertise" },
  { number: "236+", label: "Multi-site locations managed" },
  { number: ">90%", label: "Cash flow forecast accuracy" },
  { number: "£16m", label: "Max M&A transaction value" }
];

// Trust brands
const trustBrands = [
  "FTSE 100 / easyJet",
  "RBI / Tim Hortons",
  "Domino's Pizza",
  "Emirates Group"
];

// Workflow steps
const workflowSteps = [
  { number: "1", title: "Discovery Call", description: "We listen, understand your challenges, and identify opportunities." },
  { number: "2", title: "Tailored Proposal", description: "Bespoke engagement plan aligned with your specific needs and budget." },
  { number: "3", title: "Onboarding & Setup", description: "Seamless integration with your existing systems and team." },
  { number: "4", title: "Ongoing Strategic Partnership", description: "Continuous support, regular reviews, proactive insights." }
];

// Navigation component
const Navigation = ({ scrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#approach", label: "Approach" },
    { href: "#sectors", label: "Sectors" },
    { href: "/blog", label: "Blog", isRoute: true }
  ];

  // Close menu when clicking outside or on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen, setMobileMenuOpen]);

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled 
            ? "bg-[#F9F6F0]/95 backdrop-blur-md shadow-sm border-b border-[#D4D1C9]/30" 
            : "bg-[#F9F6F0]"
        }`}
      >
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" data-testid="logo-link" className="flex items-center gap-2 z-50">
              <span className="font-heading text-xl md:text-2xl font-semibold text-[#2C4A3E] tracking-tight">
                Fairway<span className="text-[#B89A5A]"> Partners</span>
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className="font-body text-sm font-medium text-[#1A1A18] hover:text-[#2C4A3E] transition-colors"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className="font-body text-sm font-medium text-[#1A1A18] hover:text-[#2C4A3E] transition-colors"
                  >
                    {link.label}
                  </a>
                )
              ))}
              <a href="#contact">
                <Button 
                  data-testid="nav-cta-button"
                  className="bg-[#2C4A3E] hover:bg-[#2C4A3E]/90 text-white font-body text-sm font-medium px-6 py-2 rounded-none btn-lift"
                >
                  Get in Touch
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 z-50 relative"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1A1A18]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A1A18]" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Full Screen */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 md:hidden"
          data-testid="mobile-menu-overlay"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-[#1A1A18]/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div 
            className="absolute top-0 right-0 h-full w-full max-w-sm bg-[#F9F6F0] shadow-2xl"
            data-testid="mobile-menu-panel"
          >
            <div className="flex flex-col h-full pt-24 px-8 pb-8">
              {/* Nav Links */}
              <div className="flex flex-col gap-6">
                {navLinks.map((link) => (
                  link.isRoute ? (
                    <Link
                      key={link.href}
                      to={link.href}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      className="font-heading text-2xl font-medium text-[#1A1A18] hover:text-[#2C4A3E] transition-colors border-b border-[#D4D1C9] pb-4"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      className="font-heading text-2xl font-medium text-[#1A1A18] hover:text-[#2C4A3E] transition-colors border-b border-[#D4D1C9] pb-4"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  )
                ))}
              </div>
              
              {/* CTA Button */}
              <div className="mt-8">
                <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    data-testid="mobile-nav-cta"
                    className="bg-[#2C4A3E] hover:bg-[#2C4A3E]/90 text-white font-body w-full py-4 rounded-none text-base"
                  >
                    Get in Touch
                  </Button>
                </a>
              </div>

              {/* Contact Info */}
              <div className="mt-auto pt-8 border-t border-[#D4D1C9]">
                <p className="font-body text-sm text-[#6B6860] mb-4">Contact us directly:</p>
                <a 
                  href="mailto:info@fairwaypartners.co.uk"
                  className="flex items-center gap-3 font-body text-sm text-[#1A1A18] hover:text-[#2C4A3E] mb-3"
                >
                  <Mail className="w-4 h-4" />
                  info@fairwaypartners.co.uk
                </a>
                <a 
                  href="tel:07440187478"
                  className="flex items-center gap-3 font-body text-sm text-[#1A1A18] hover:text-[#2C4A3E]"
                >
                  <Phone className="w-4 h-4" />
                  07440 187478
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="min-h-screen pt-20 hero-bg-pattern"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            <div className="animate-fade-in-up">
              <span className="font-body text-sm font-medium tracking-widest uppercase text-[#B89A5A]">
                CFO-Level Strategic Finance
              </span>
            </div>
            
            <h1 
              data-testid="hero-heading"
              className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-[#1A1A18] animate-fade-in-up animation-delay-100"
            >
              The Fair Advantage<br />for Your Business
            </h1>
            
            <p 
              data-testid="hero-subheading"
              className="font-body text-lg md:text-xl leading-relaxed text-[#6B6860] max-w-xl animate-fade-in-up animation-delay-200"
            >
              Enterprise-level financial leadership at accessible pricing — from sole traders to £50m+ operations across 17 years and global brands.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <a href="#contact">
                <Button 
                  data-testid="hero-cta-primary"
                  className="bg-[#2C4A3E] hover:bg-[#2C4A3E]/90 text-white font-body text-base font-medium px-8 py-6 rounded-none btn-lift w-full sm:w-auto"
                >
                  Book a Consultation
                </Button>
              </a>
              <a href="#services">
                <Button 
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="border-[#2C4A3E] text-[#2C4A3E] hover:bg-[#2C4A3E] hover:text-white font-body text-base font-medium px-8 py-6 rounded-none w-full sm:w-auto"
                >
                  Our Services
                </Button>
              </a>
            </div>
          </div>

          {/* Right Column - Stats */}
          <div className="grid grid-cols-2 gap-6 lg:gap-8 animate-fade-in-up animation-delay-400">
            {stats.map((stat, index) => (
              <div 
                key={index}
                data-testid={`stat-card-${index}`}
                className="stat-card bg-white/60 backdrop-blur-sm p-6 lg:p-8"
              >
                <div className="font-heading text-4xl lg:text-5xl font-semibold text-[#2C4A3E] tracking-tight">
                  {stat.number}
                </div>
                <div className="font-body text-sm text-[#6B6860] mt-2">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust Bar Section
const TrustBar = () => {
  return (
    <section 
      data-testid="trust-bar"
      className="bg-[#2C4A3E] py-8 md:py-10"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 lg:gap-24">
          {trustBrands.map((brand, index) => (
            <span 
              key={index}
              data-testid={`trust-brand-${index}`}
              className="font-body text-sm md:text-base font-medium text-white/80 tracking-wide trust-brand"
            >
              {brand}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section 
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32 bg-[#F9F6F0]"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mb-16">
          <span className="font-body text-sm font-medium tracking-widest uppercase text-[#B89A5A]">
            What We Do
          </span>
          <h2 
            data-testid="services-heading"
            className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-[#1A1A18] mt-4"
          >
            Strategic Finance Services
          </h2>
          <p className="font-body text-lg text-[#6B6860] mt-4 leading-relaxed">
            Comprehensive financial leadership tailored to your business stage and ambitions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#D4D1C9]">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="service-card-wrapper relative"
              >
                <Icon className="w-8 h-8 text-[#2C4A3E] stroke-[1.5] mb-4" />
                <h3 className="font-heading text-xl font-medium text-[#1A1A18] mb-2">
                  {service.title}
                </h3>
                <p className="font-body text-sm text-[#6B6860] leading-relaxed">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Approach Section
const ApproachSection = () => {
  return (
    <section 
      id="approach"
      data-testid="approach-section"
      className="py-20 md:py-32 bg-[#E8E4D9]/30"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Workflow */}
          <div>
            <span className="font-body text-sm font-medium tracking-widest uppercase text-[#B89A5A]">
              Our Approach
            </span>
            <h2 
              data-testid="approach-heading"
              className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-[#1A1A18] mt-4 mb-12"
            >
              How We Work
            </h2>

            <div className="space-y-10">
              {workflowSteps.map((step, index) => (
                <div 
                  key={index}
                  data-testid={`workflow-step-${index}`}
                  className="relative pl-12"
                >
                  {/* Vertical line */}
                  {index < workflowSteps.length - 1 && (
                    <div className="absolute left-[0.6rem] top-8 bottom-0 w-[2px] bg-[#D4D1C9]" />
                  )}
                  
                  {/* Step number */}
                  <div className="absolute left-0 top-0 w-6 h-6 bg-[#2C4A3E] text-white rounded-full flex items-center justify-center text-xs font-bold font-body">
                    {step.number}
                  </div>
                  
                  <h3 className="font-heading text-xl font-medium text-[#1A1A18] mb-1">
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-[#6B6860] leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Quote Panel */}
          <div className="bg-[#2C4A3E] p-10 lg:p-14 flex flex-col justify-between">
            <div>
              <blockquote 
                data-testid="founder-quote"
                className="font-heading text-2xl md:text-3xl font-light text-white leading-relaxed italic"
              >
                "We bring the rigour of FTSE 100 finance functions to businesses of every size. No jargon, no fluff — just clear numbers and strategic insight that drives results."
              </blockquote>
            </div>
            
            <div className="mt-10 pt-8 border-t border-white/20">
              <div className="font-heading text-xl font-medium text-white">
                Kuldeepsinh Jhala
              </div>
              <div className="font-body text-sm text-white/70 mt-1">
                FCCA · ACA · MBA · 17 Years
              </div>
              <div className="flex gap-4 mt-6">
                <span className="font-body text-xs text-white/50 uppercase tracking-wider border border-white/30 px-3 py-1">FCCA</span>
                <span className="font-body text-xs text-white/50 uppercase tracking-wider border border-white/30 px-3 py-1">ACA</span>
                <span className="font-body text-xs text-white/50 uppercase tracking-wider border border-white/30 px-3 py-1">MBA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sectors Section
const SectorsSection = () => {
  return (
    <section 
      id="sectors"
      data-testid="sectors-section"
      className="py-20 md:py-32 bg-[#1A1A18]"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mb-16">
          <span className="font-body text-sm font-medium tracking-widest uppercase text-[#B89A5A]">
            Industry Expertise
          </span>
          <h2 
            data-testid="sectors-heading"
            className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-white mt-4"
          >
            Sectors We Serve
          </h2>
          <p className="font-body text-lg text-white/60 mt-4 leading-relaxed">
            Deep experience across industries that demand financial precision and strategic agility.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sectors.map((sector, index) => {
            const Icon = sector.icon;
            return (
              <div
                key={index}
                data-testid={`sector-tile-${index}`}
                className="sector-tile bg-white/5 border border-white/10 p-8 cursor-pointer"
              >
                <Icon className="w-8 h-8 text-[#B89A5A] stroke-[1.5] mb-4" />
                <h3 className="font-heading text-xl font-medium text-white mb-2">
                  {sector.title}
                </h3>
                <p className="font-body text-sm text-white/60 leading-relaxed">
                  {sector.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Cookie Consent Banner
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 bg-[#1A1A18] text-white p-6 z-50 shadow-lg"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <Cookie className="w-6 h-6 text-[#B89A5A] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-heading text-lg font-medium mb-1">We value your privacy</h3>
              <p className="font-body text-sm text-white/70">
                We use cookies to enhance your browsing experience and analyse our traffic. 
                By clicking "Accept All", you consent to our use of cookies. 
                Read our <Link to="/privacy-policy" className="text-[#B89A5A] hover:underline">Privacy Policy</Link> and <Link to="/cookie-policy" className="text-[#B89A5A] hover:underline">Cookie Policy</Link>.
              </p>
            </div>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button
              data-testid="cookie-decline-btn"
              onClick={declineCookies}
              className="px-6 py-2 border border-white/30 text-white font-body text-sm hover:bg-white/10 transition-colors"
            >
              Decline
            </button>
            <button
              data-testid="cookie-accept-btn"
              onClick={acceptCookies}
              className="px-6 py-2 bg-[#B89A5A] text-white font-body text-sm hover:bg-[#B89A5A]/90 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Section
const ContactSection = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const serviceOptions = [
    "Bookkeeping & Management Accounts",
    "Tax Planning & Compliance",
    "Financial Planning & Analysis",
    "Cash Flow & Working Capital",
    "Mergers & Acquisitions",
    "Finance Recruitment & Coaching",
    "Financial Systems & ERP",
    "Turnaround & Restructuring",
    "Other"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      alert("Please agree to the Privacy Policy to submit the form.");
      return;
    }
    
    setIsSubmitting(true);
    
    const formData = new FormData(e.target);
    formData.append("service", selectedService);
    
    try {
      await fetch("https://formsubmit.co/ajax/k.jhala@outlook.com", {
        method: "POST",
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      setFormSubmitted(true);
    } catch (error) {
      console.error("Form submission error:", error);
      // Still show success since FormSubmit might have received it
      setFormSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#F9F6F0]"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Contact Info */}
          <div>
            <span className="font-body text-sm font-medium tracking-widest uppercase text-[#B89A5A]">
              Get in Touch
            </span>
            <h2 
              data-testid="contact-heading"
              className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-[#1A1A18] mt-4 mb-8"
            >
              Let's Discuss Your<br />Financial Strategy
            </h2>
            
            <div className="space-y-6">
              <a 
                href="mailto:info@fairwaypartners.co.uk"
                data-testid="contact-email"
                className="flex items-center gap-4 font-body text-[#1A1A18] hover:text-[#2C4A3E] transition-colors group"
              >
                <Mail className="w-5 h-5 text-[#6B6860] group-hover:text-[#2C4A3E]" />
                info@fairwaypartners.co.uk
              </a>
              
              <a 
                href="tel:07440187478"
                data-testid="contact-phone"
                className="flex items-center gap-4 font-body text-[#1A1A18] hover:text-[#2C4A3E] transition-colors group"
              >
                <Phone className="w-5 h-5 text-[#6B6860] group-hover:text-[#2C4A3E]" />
                07440 187478
              </a>
              
              <a 
                href="https://linkedin.com/in/kuldeepjhala"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin"
                className="flex items-center gap-4 font-body text-[#1A1A18] hover:text-[#2C4A3E] transition-colors group"
              >
                <Linkedin className="w-5 h-5 text-[#6B6860] group-hover:text-[#2C4A3E]" />
                linkedin.com/in/kuldeepjhala
              </a>
            </div>

            {/* Image */}
            <div className="mt-12 hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1758518727613-00192aed759b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBmaW5hbmNlJTIwbWVldGluZyUyMGJvYXJkcm9vbSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzE0MDk0OTN8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional consultation"
                className="w-full h-64 object-cover grayscale opacity-80"
              />
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white p-8 lg:p-12 border border-[#D4D1C9]">
            {formSubmitted ? (
              <div 
                data-testid="form-success-message"
                className="success-message text-center py-16"
              >
                <div className="w-16 h-16 bg-[#2C4A3E] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-medium text-[#1A1A18] mb-2">
                  Thank you!
                </h3>
                <p className="font-body text-[#6B6860]">
                  We'll be in touch within one business day.
                </p>
              </div>
            ) : (
              <form 
                data-testid="contact-form"
                onSubmit={handleSubmit}
              >
                {/* Hidden fields for FormSubmit */}
                <input type="hidden" name="_cc" value="ajayvgupta96@gmail.com" />
                <input type="hidden" name="_subject" value="New Enquiry – Fairway Partners Website" />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_template" value="table" />
                <input type="text" name="_honey" style={{ display: 'none' }} />
                
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="font-body text-sm text-[#6B6860] block mb-2">First Name</label>
                    <input 
                      type="text"
                      name="first_name"
                      required
                      data-testid="form-first-name"
                      className="form-input"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-[#6B6860] block mb-2">Last Name</label>
                    <input 
                      type="text"
                      name="last_name"
                      required
                      data-testid="form-last-name"
                      className="form-input"
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="font-body text-sm text-[#6B6860] block mb-2">Email Address</label>
                  <input 
                    type="email"
                    name="email"
                    required
                    data-testid="form-email"
                    className="form-input"
                    placeholder="john@company.com"
                  />
                </div>

                <div className="mb-6">
                  <label className="font-body text-sm text-[#6B6860] block mb-2">Phone Number</label>
                  <input 
                    type="tel"
                    name="phone"
                    data-testid="form-phone"
                    className="form-input"
                    placeholder="+44 7123 456789"
                  />
                </div>

                <div className="mb-6">
                  <label className="font-body text-sm text-[#6B6860] block mb-2">Service of Interest</label>
                  <Select value={selectedService} onValueChange={setSelectedService}>
                    <SelectTrigger 
                      data-testid="form-service-select"
                      className="w-full border-0 border-b border-[#D4D1C9] rounded-none px-0 py-3 font-body text-base focus:ring-0 focus:border-[#2C4A3E] bg-transparent"
                    >
                      <SelectValue placeholder="Select a service..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-[#D4D1C9]">
                      {serviceOptions.map((service) => (
                        <SelectItem 
                          key={service} 
                          value={service}
                          className="font-body cursor-pointer"
                        >
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" name="service" value={selectedService} />
                </div>

                <div className="mb-8">
                  <label className="font-body text-sm text-[#6B6860] block mb-2">Message</label>
                  <textarea 
                    name="message"
                    rows={4}
                    data-testid="form-message"
                    className="form-input resize-none"
                    placeholder="Tell us about your business and how we can help..."
                  />
                </div>

                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      data-testid="form-consent-checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#2C4A3E] cursor-pointer"
                    />
                    <span className="font-body text-sm text-[#6B6860] leading-relaxed">
                      I agree to the <Link to="/privacy-policy" className="text-[#2C4A3E] hover:underline">Privacy Policy</Link> and consent to Fairway Partners processing my personal data in accordance with GDPR regulations. <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>

                <Button 
                  type="submit"
                  data-testid="form-submit-button"
                  disabled={isSubmitting}
                  className="w-full bg-[#2C4A3E] hover:bg-[#2C4A3E]/90 text-white font-body text-base font-medium py-6 rounded-none btn-lift disabled:opacity-70"
                >
                  {isSubmitting ? "Sending..." : "Send Enquiry"}
                  {!isSubmitting && <ChevronRight className="w-4 h-4 ml-2" />}
                </Button>

                <p className="font-body text-xs text-[#6B6860] text-center mt-4">
                  We typically respond within one business day.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer 
      data-testid="footer"
      className="bg-[#1A1A18] py-12"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-4 gap-8 mb-10">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <div className="font-heading text-xl font-semibold text-white tracking-tight mb-4">
              Fairway<span className="text-[#B89A5A]"> Partners</span>
            </div>
            <p className="font-body text-sm text-white/60 leading-relaxed max-w-md">
              CFO-level strategic finance consultancy providing enterprise-grade financial leadership to businesses from sole traders to £50m+ operations.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-body text-sm font-semibold text-white uppercase tracking-wider mb-4">Navigation</h4>
            <div className="flex flex-col gap-2">
              <a href="#services" className="font-body text-sm text-white/60 hover:text-white transition-colors">
                Services
              </a>
              <a href="#approach" className="font-body text-sm text-white/60 hover:text-white transition-colors">
                Approach
              </a>
              <a href="#sectors" className="font-body text-sm text-white/60 hover:text-white transition-colors">
                Sectors
              </a>
              <a href="#contact" className="font-body text-sm text-white/60 hover:text-white transition-colors">
                Contact
              </a>
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-body text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
            <div className="flex flex-col gap-2">
              <Link to="/privacy-policy" data-testid="footer-privacy-link" className="font-body text-sm text-white/60 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" data-testid="footer-cookie-link" className="font-body text-sm text-white/60 hover:text-white transition-colors">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="font-body text-sm text-white/40">
            © {new Date().getFullYear()} Fairway Partners Limited. All rights reserved.
          </div>
          <div className="font-body text-xs text-white/30">
            Registered in England & Wales
          </div>
        </div>
      </div>
    </footer>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <Link to="/" className="font-body text-sm text-[#2C4A3E] hover:underline flex items-center gap-2">
              ← Back to Home
            </Link>
          </div>
          
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-[#2C4A3E]" />
            <h1 data-testid="privacy-policy-heading" className="font-heading text-4xl md:text-5xl font-normal text-[#1A1A18]">
              Privacy Policy
            </h1>
          </div>
          
          <p className="font-body text-sm text-[#6B6860] mb-12">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">1. Introduction</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                Fairway Partners Limited ("we", "our", "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our services.
              </p>
              <p className="font-body text-[#6B6860] leading-relaxed">
                We are registered in England and Wales and act as the data controller for the personal data we process. We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">2. Information We Collect</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                We may collect and process the following categories of personal data:
              </p>
              <ul className="font-body text-[#6B6860] space-y-2 list-disc pl-6">
                <li><strong>Identity Data:</strong> First name, last name, title, company name</li>
                <li><strong>Contact Data:</strong> Email address, telephone number, business address</li>
                <li><strong>Technical Data:</strong> IP address, browser type and version, time zone setting, browser plug-in types, operating system</li>
                <li><strong>Usage Data:</strong> Information about how you use our website</li>
                <li><strong>Marketing Data:</strong> Your preferences in receiving marketing from us</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">3. How We Use Your Information</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                We use your personal data for the following purposes:
              </p>
              <ul className="font-body text-[#6B6860] space-y-2 list-disc pl-6">
                <li>To respond to your enquiries and provide our services</li>
                <li>To send you information about our services that may be of interest to you</li>
                <li>To improve our website and services</li>
                <li>To comply with legal obligations</li>
                <li>To protect our legitimate business interests</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">4. Legal Basis for Processing</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                We process your personal data on the following legal bases:
              </p>
              <ul className="font-body text-[#6B6860] space-y-2 list-disc pl-6">
                <li><strong>Consent:</strong> Where you have given explicit consent for us to process your data</li>
                <li><strong>Contract:</strong> Where processing is necessary for a contract we have with you</li>
                <li><strong>Legal Obligation:</strong> Where we need to comply with a legal obligation</li>
                <li><strong>Legitimate Interests:</strong> Where it is necessary for our legitimate business interests</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">5. Data Sharing</h2>
              <p className="font-body text-[#6B6860] leading-relaxed">
                We do not sell your personal data. We may share your data with trusted third-party service providers who assist us in operating our website and conducting our business, subject to confidentiality agreements. We may also disclose your data where required by law or to protect our rights.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">6. Data Retention</h2>
              <p className="font-body text-[#6B6860] leading-relaxed">
                We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements. For contact form enquiries, we typically retain data for 3 years unless you request earlier deletion.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">7. Your Rights</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                Under UK GDPR, you have the following rights:
              </p>
              <ul className="font-body text-[#6B6860] space-y-2 list-disc pl-6">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Restrict Processing:</strong> Request limitation of processing</li>
                <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">8. Data Security</h2>
              <p className="font-body text-[#6B6860] leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">9. International Transfers</h2>
              <p className="font-body text-[#6B6860] leading-relaxed">
                Your data may be transferred to and processed in countries outside the UK. Where this occurs, we ensure appropriate safeguards are in place to protect your data in accordance with UK GDPR requirements.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">10. Contact Us</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
              </p>
              <div className="font-body text-[#6B6860] bg-[#E8E4D9]/30 p-6">
                <p className="mb-2"><strong>Fairway Partners Limited</strong></p>
                <p className="mb-2">Email: info@fairwaypartners.co.uk</p>
                <p className="mb-2">Phone: 07440 187478</p>
              </div>
              <p className="font-body text-[#6B6860] leading-relaxed mt-4">
                You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#2C4A3E] hover:underline">ico.org.uk</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cookie Policy Page
const CookiePolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <Link to="/" className="font-body text-sm text-[#2C4A3E] hover:underline flex items-center gap-2">
              ← Back to Home
            </Link>
          </div>
          
          <div className="flex items-center gap-3 mb-6">
            <Cookie className="w-8 h-8 text-[#2C4A3E]" />
            <h1 data-testid="cookie-policy-heading" className="font-heading text-4xl md:text-5xl font-normal text-[#1A1A18]">
              Cookie Policy
            </h1>
          </div>
          
          <p className="font-body text-sm text-[#6B6860] mb-12">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">1. What Are Cookies?</h2>
              <p className="font-body text-[#6B6860] leading-relaxed">
                Cookies are small text files that are stored on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners. Cookies help us improve your experience on our website.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">2. How We Use Cookies</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                Fairway Partners Limited uses cookies for the following purposes:
              </p>
              <ul className="font-body text-[#6B6860] space-y-2 list-disc pl-6">
                <li>To remember your cookie consent preferences</li>
                <li>To analyse website traffic and usage patterns</li>
                <li>To improve the functionality and performance of our website</li>
                <li>To provide a better user experience</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">3. Types of Cookies We Use</h2>
              
              <div className="mb-6">
                <h3 className="font-heading text-xl font-medium text-[#1A1A18] mb-3">Essential Cookies</h3>
                <p className="font-body text-[#6B6860] leading-relaxed">
                  These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas. The website cannot function properly without these cookies.
                </p>
                <div className="mt-4 bg-[#E8E4D9]/30 p-4 rounded">
                  <table className="w-full font-body text-sm">
                    <thead>
                      <tr className="text-left border-b border-[#D4D1C9]">
                        <th className="pb-2 text-[#1A1A18]">Cookie Name</th>
                        <th className="pb-2 text-[#1A1A18]">Purpose</th>
                        <th className="pb-2 text-[#1A1A18]">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-[#6B6860]">
                      <tr>
                        <td className="py-2">cookieConsent</td>
                        <td className="py-2">Stores your cookie consent preference</td>
                        <td className="py-2">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-heading text-xl font-medium text-[#1A1A18] mb-3">Analytics Cookies</h3>
                <p className="font-body text-[#6B6860] leading-relaxed">
                  These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and services.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="font-heading text-xl font-medium text-[#1A1A18] mb-3">Functional Cookies</h3>
                <p className="font-body text-[#6B6860] leading-relaxed">
                  These cookies enable enhanced functionality and personalisation, such as remembering your preferences. They may be set by us or by third-party providers whose services we have added to our pages.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">4. Third-Party Cookies</h2>
              <p className="font-body text-[#6B6860] leading-relaxed">
                Some cookies are placed by third-party services that appear on our pages. We use FormSubmit.co for our contact form, which may set its own cookies to process form submissions. We do not control these third-party cookies. Please refer to the respective third-party privacy policies for more information.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">5. Managing Cookies</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                You can control and manage cookies in several ways:
              </p>
              <ul className="font-body text-[#6B6860] space-y-2 list-disc pl-6">
                <li><strong>Cookie Banner:</strong> Use our cookie consent banner to accept or decline non-essential cookies</li>
                <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies through their settings</li>
                <li><strong>Device Settings:</strong> Mobile devices typically provide cookie management options</li>
              </ul>
              <p className="font-body text-[#6B6860] leading-relaxed mt-4">
                Please note that disabling cookies may affect the functionality of our website and your user experience.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">6. Browser-Specific Instructions</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                To manage cookies in your browser, follow these links:
              </p>
              <ul className="font-body text-[#6B6860] space-y-2 list-disc pl-6">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#2C4A3E] hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-[#2C4A3E] hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#2C4A3E] hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-gb/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#2C4A3E] hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">7. Updates to This Policy</h2>
              <p className="font-body text-[#6B6860] leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our business practices. Any changes will be posted on this page with an updated revision date.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-medium text-[#1A1A18] mb-4">8. Contact Us</h2>
              <p className="font-body text-[#6B6860] leading-relaxed mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="font-body text-[#6B6860] bg-[#E8E4D9]/30 p-6">
                <p className="mb-2"><strong>Fairway Partners Limited</strong></p>
                <p className="mb-2">Email: info@fairwaypartners.co.uk</p>
                <p className="mb-2">Phone: 07440 187478</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Navigation 
        scrolled={scrolled} 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <HeroSection />
      <TrustBar />
      <ServicesSection />
      <ApproachSection />
      <SectorsSection />
      <ContactSection />
    </>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <div className="App font-body">
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<><HomePage /><Footer /><CookieConsent /></>} />
          <Route path="/privacy-policy" element={<><PrivacyPolicy /><Footer /><CookieConsent /></>} />
          <Route path="/cookie-policy" element={<><CookiePolicy /><Footer /><CookieConsent /></>} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/posts/new" element={<AdminPostEditor />} />
          <Route path="/admin/posts/:postId/edit" element={<AdminPostEditor />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
